import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { logEvent } from '~/config/firebase.config';
import { resumeSubscription, SubscriptionActionError } from '~/services/subscription/subscriptionActions';

export type ResumeError = { message: string; portalUrl?: string } | null;

/**
 * « Reprendre maintenant » / « Annuler la pause » : appel serveur + messages
 * d'erreur prêts à afficher (carte refusée → lien portail Stripe).
 */
export function useResumeSubscription(clientID: string, origin: 'account' | 'cancel_flow') {
    const { t, i18n } = useTranslation();
    const [resuming, setResuming] = useState(false);
    const [error, setError] = useState<ResumeError>(null);

    /**
     * `phase` = ce qui était affiché au clic (recalculé à cet instant par
     * l'appelant). Le serveur refuse si la pause a commencé entre-temps :
     * jamais de prélèvement que l'écran n'annonçait pas.
     */
    const resume = async (phase: 'scheduled' | 'active'): Promise<'ok' | 'failed' | 'changed'> => {
        logEvent('pause_resume_click', origin, { clientID, phase });
        setResuming(true);
        setError(null);
        try {
            await resumeSubscription(
                clientID,
                i18n.language,
                phase === 'scheduled' ? 'cancel_before_start' : 'resume_early'
            );
            return 'ok';
        } catch (err) {
            const code = err instanceof SubscriptionActionError ? err.code : 'unknown';
            if (code === 'payment_failed') {
                setError({
                    message: t('account.pause.paymentFailed'),
                    portalUrl: (err as SubscriptionActionError).portalUrl,
                });
            } else if (code === 'forbidden') {
                setError({ message: t('account.pause.adminOnly') });
            } else if (code === 'phase_mismatch') {
                // La pause a commencé (ou été levée) entre l'affichage et le clic : on relit.
                setError({ message: t('account.pause.phaseChanged') });
                return 'changed';
            } else if (code === 'not_paused') {
                // Déjà reprise (autre onglet, reprise automatique) : on relit l'état.
                return 'changed';
            } else {
                setError({ message: t('account.pause.error') });
            }
            return 'failed';
        } finally {
            setResuming(false);
        }
    };

    return { resume, resuming, error };
}
